<template>
    <div class="mobile-search-form-popup">
        <div class="mobile-search-form-popup__header">
            <svg-icon name="close" @click="$emit('close')" />
        </div>
        <div ref="mobileSearchFormContent" class="mobile-search-form-popup__content">
            <div
                class="mobile-search-form-popup__filter"
                :class="{ 'mobile-search-form-popup__filter--active': currentStep === STEP_LOCATION }"
                @click="activateInput(STEP_LOCATION)"
            >
                <div v-if="currentStep === STEP_LOCATION" class="mobile-search-form-popup__location">
                    <label class="label">{{ $t('search_form.location_label') }}</label>
                    <LocationFilter @location-change="handleLocationChange" />
                </div>
                <div v-else class="mobile-search-form-popup__overview-element">
                    <svg-icon name="location" class="mobile-search-form-popup__icon" />
                    <span>{{ locationLabel }}</span>
                </div>
            </div>

            <div
                class="mobile-search-form-popup__filter"
                :class="{ 'mobile-search-form-popup__filter--active': currentStep === STEP_DATES }"
                @click="activateInput(STEP_DATES)"
            >
                <div v-if="currentStep === STEP_DATES" class="mobile-search-form-popup__dates">
                    <div class="mobile-search-form-popup__dates__datepicker">
                        <label class="label">{{ $t('search_form.dates_label') }}</label>
                        <DateRange
                            :start="start"
                            :end="end"
                            :with-focus="true"
                            mode="input"
                            :dash-separator="true"
                            :has-icon="true"
                        />

                        <McDatepicker
                            id="mobile-search-datepicker-filter"
                            ref="mobileSearchFormDatepicker"
                            :date-one="start"
                            :date-two="end"
                            :start-open="true"
                            type="inline"
                            :mobile-months-to-show="12"
                            :show-header="false"
                            :show-action-buttons="false"
                            :is-in-modal="true"
                            :full-screen-mobile="false"
                            :min-date="minFromDate"
                            @date-one-selected="handleDateOne"
                            @date-two-selected="handleDateTwo"
                        />
                    </div>

                    <!-- <FlexibleDatesFilter /> -->
                </div>

                <div v-else class="mobile-search-form-popup__overview-element">
                    <svg-icon name="datepicker" class="mobile-search-form-popup__icon" />
                    <span>{{ datesLabel }}</span>
                </div>
            </div>
        </div>

        <div class="mobile-search-form-popup__actions">
            <div>
                <a v-if="currentStep === STEP_LOCATION" @click.prevent="currentStep = STEP_DATES">
                    {{ $t('buttons.skip') }}
                </a>
                <a v-if="currentStep === STEP_DATES && (start || end)" @click.prevent="removeDates">
                    {{ $t('search_form.remove_dates') }}
                </a>
            </div>
            <div>
                <button class="button button--primary" @click="$emit('search')">
                    {{ $t('search_form.button') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

import startOfTomorrow from 'date-fns/startOfTomorrow';
import formatISO from 'date-fns/formatISO';
import isBefore from 'date-fns/isBefore';
import isDate from 'date-fns/isDate';
import DateRange from '~/components/DateRange.vue';
import McDatepicker from '~/components/form-elements/datepicker/McDatepicker.vue';
import LocationFilter from '~/components/vehicles/LocationFilter.vue';
import { getDefaultFilterValue, getFilterValue } from '~/helpers/queryParser';
import { AVAILABLE_BETWEEN } from '~/config/filterKeys';
import { actionTypes as filterActionTypes } from '~/store/filters';

const STEP_LOCATION = 'location';
const STEP_DATES = 'dates';

export default {
    name: 'MobileSearchFormPopup',
    components: { LocationFilter, McDatepicker, DateRange },
    props: {
        locationLabel: {
            type: String,
            default: '',
        },
        datesLabel: {
            type: String,
            default: '',
        },
        datesStepActive: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const dates = getFilterValue(this.$route.query, AVAILABLE_BETWEEN);

        return {
            currentStep: this.datesStepActive ? STEP_DATES : STEP_LOCATION,
            start: dates?.from,
            end: dates?.to,
            STEP_LOCATION,
            STEP_DATES,
        };
    },
    computed: {
        minFromDate() {
            return formatISO(startOfTomorrow(), { representation: 'date' });
        },
    },
    watch: {
        currentStep: {
            handler() {
                if (this.currentStep === STEP_DATES) {
                    this.$nextTick(() => {
                        this.calculateDatepickerHeight();
                    });
                }
            },
            immediate: true,
        },
    },
    mounted() {
        disableBodyScroll(this.$refs.mobileSearchFormContent, {
            // If any datepicker element is touched, allow scrolling
            allowTouchMove: el => Array.from(el.classList).some(className => className.startsWith('mcd__')),
        });

        window.addEventListener('resize', this.calculateDatepickerHeight);
    },
    beforeDestroy() {
        clearAllBodyScrollLocks();

        window.removeEventListener('resize', this.calculateDatepickerHeight);
    },
    methods: {
        handleDateOne(value) {
            this.start = value;
        },
        handleDateTwo(value) {
            this.end = value;

            if (this.start && this.end) {
                this.storeSelectedDates(this.start, this.end);
            }
        },
        storeSelectedDates(from, to) {
            let dates = {
                from,
                to,
            };

            const fromDate = new Date(from);
            const toDate = new Date(to);

            // Make sure to is after from
            if (isDate(fromDate) && isDate(toDate) && isBefore(toDate, fromDate)) {
                // A swap is required
                dates = {
                    from: to,
                    to: from,
                };
            }

            if (!from || !to) {
                dates = getDefaultFilterValue(AVAILABLE_BETWEEN);
            }

            this.$store
                .dispatch(filterActionTypes.COLLECT_STAND_BY_FILTERS, {
                    [AVAILABLE_BETWEEN]: dates,
                })
                .catch(err => {
                    this.$sentry.captureException(err);
                });
        },
        removeDates() {
            this.start = null;
            this.end = null;
            this.storeSelectedDates(this.start, this.end);

            this.$refs.mobileSearchFormDatepicker?.clearSelection();
        },
        activateInput(step) {
            if (this.currentStep === step) {
                return;
            }

            this.currentStep = step;
        },
        handleLocationChange(event) {
            if (event) {
                this.currentStep = STEP_DATES;
            }
        },
        calculateDatepickerHeight() {
            if (this.currentStep !== STEP_DATES) {
                return;
            }

            this.$refs.mobileSearchFormContent.scrollTop = 0; // doesnt work otherwise

            const contentWrapper = document.querySelector('#mobile-search-datepicker-filter .mcd__content-wrapper');
            const actionsElement = document.querySelector('.mobile-search-form-popup__actions');

            if (!contentWrapper || !actionsElement) {
                return;
            }

            const height = actionsElement.getBoundingClientRect().top - contentWrapper.getBoundingClientRect().top - 20; // 20 (margin)

            contentWrapper.style.height = `${height}px`;
        },
    },
};
</script>
<style lang="scss">
@import '@/sass/_variables.scss';
.mobile-search-form-popup {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: $z-modal;
    background: $white;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 20px 0 0;
    box-sizing: border-box;

    &__header {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 8px;
        padding: 0 20px;

        .svg-icon {
            width: 32px;
            height: 32px;
        }
    }

    &__content {
        overflow: auto;
        padding: 0 20px 24px;
        height: 100%;
    }

    &__overview-element {
        display: flex;
        align-items: center;
    }

    &__filter {
        position: relative;
        display: flex;
        align-items: center;
        padding: 12px 20px;
        border: 1px solid $border-color;
        border-radius: $default-border-radius;
        margin-top: 24px;

        &--active {
            padding: 20px 16px;
        }

        &:first-child {
            margin-top: 0;
        }
    }

    &__icon {
        min-width: 24px;
        min-height: 24px;
        margin-right: 8px;
    }

    &__actions {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: auto;
        border-top: 1px solid $border-color;
        padding: 16px 20px;
        box-sizing: border-box;
        background: $white;

        button.button--primary {
            padding: 11px 24px;
            width: auto;
        }

        a {
            color: $darkest-gray;
        }
    }

    &__location {
        width: 100%;

        .location-input__find-my-position {
            width: auto !important;
        }
    }

    &__dates {
        position: absolute;
        top: -1px;
        left: 0;
        width: calc(100% + 2px);
        margin: 0 -1px;
        z-index: 1;
        box-sizing: border-box;
        background: $white;

        &__datepicker {
            border: 1px solid $border-color;
            border-radius: $default-border-radius;
            padding: 20px 16px 1px;
            margin-bottom: 16px;

            .mcd {
                margin-top: 24px;

                &__wrapper {
                    border: none;
                }

                &__month {
                    padding: 16px 0;
                    min-width: unset;
                }

                &__change-month-button {
                    display: none;
                }

                &__week-number,
                &__week-title,
                &__day-title {
                    @include font-size(12px);
                }

                &__day-button {
                    @include font-size(14px);
                }
            }
        }
    }
}
</style>
